import type { Projection, YearRow } from '../calc/types';
import { formatMoney, formatMoneyCompact } from './format';

const NS = 'http://www.w3.org/2000/svg';

const PAD = { top: 16, right: 16, bottom: 32, left: 56 };
const MIN_HEIGHT = 220;
const MAX_HEIGHT = 340;
/** Below this vertical gap the two end-labels would collide, so we drop them. */
const LABEL_GAP = 16;

interface Series {
  key: 'nominal' | 'real';
  label: string;
  /** Categorical palette slot — the colour itself lives in CSS. */
  slot: 1 | 2;
  value: (row: YearRow) => number;
}

const SERIES: Series[] = [
  { key: 'nominal', label: 'Pot value', slot: 1, value: (r) => r.balanceNominal },
  { key: 'real', label: "In today's money", slot: 2, value: (r) => r.balanceReal },
];

/**
 * Line chart of the pot by age: nominal against today's money.
 * Hand-rolled SVG — a chart library would outweigh the whole app.
 */
export function renderChart(host: HTMLElement, projection: Projection, currency: string): void {
  const width = Math.max(280, host.clientWidth || 640);
  const height = Math.round(Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, width * 0.5)));
  const plotW = width - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;

  const rows = projection.rows;
  const maxValue = Math.max(
    1,
    ...rows.map((r) => Math.max(r.balanceNominal, r.balanceReal)),
  );
  const ticks = niceTicks(maxValue);
  const yMax = ticks[ticks.length - 1] ?? maxValue;

  const firstAge = rows[0]?.age ?? 0;
  const lastAge = rows[rows.length - 1]?.age ?? firstAge;
  const ageSpan = Math.max(1, lastAge - firstAge);

  const x = (age: number) => PAD.left + ((age - firstAge) / ageSpan) * plotW;
  const y = (value: number) => PAD.top + plotH - (value / yMax) * plotH;

  const svg = el('svg', {
    viewBox: `0 0 ${width} ${height}`,
    width: String(width),
    height: String(height),
    class: 'chart__svg',
    role: 'img',
    tabindex: '0',
    'aria-label': summarise(projection, currency),
  });

  // Gridlines + y axis labels
  for (const tick of ticks) {
    const ty = y(tick);
    svg.append(
      el('line', { x1: String(PAD.left), x2: String(PAD.left + plotW), y1: String(ty), y2: String(ty), class: 'chart__grid' }),
      text(String(PAD.left - 8), String(ty + 4), formatMoneyCompact(tick, currency), 'chart__tick chart__tick--y'),
    );
  }

  // X axis labels — first, last, and a few in between
  for (const age of ageTicks(firstAge, lastAge)) {
    svg.append(text(String(x(age)), String(height - 10), String(age), 'chart__tick chart__tick--x'));
  }
  svg.append(
    el('line', {
      x1: String(PAD.left), x2: String(PAD.left + plotW),
      y1: String(PAD.top + plotH), y2: String(PAD.top + plotH),
      class: 'chart__axis',
    }),
  );

  // Stage boundaries: dashed guides where the contribution schedule changes.
  const boundaryAges = new Set<number>();
  for (const stage of projection.stages.slice(0, -1)) {
    if (stage.end > firstAge && stage.end < lastAge) boundaryAges.add(stage.end);
  }

  // Series: a 10% wash under a 2px line.
  for (const series of SERIES) {
    const points = rows.map((r) => [x(r.age), y(series.value(r))] as const);
    svg.append(
      el('path', {
        d: `${line(points)} L ${points[points.length - 1]?.[0] ?? 0} ${PAD.top + plotH} L ${points[0]?.[0] ?? 0} ${PAD.top + plotH} Z`,
        class: `chart__area chart__area--${series.slot}`,
      }),
      el('path', {
        d: line(points),
        class: `chart__line chart__line--${series.slot}`,
      }),
    );
  }

  // Drawn on top of the area fills so they stay visible.
  for (const age of boundaryAges) {
    const bx = x(age);
    svg.append(
      el('line', {
        x1: String(bx), x2: String(bx),
        y1: String(PAD.top), y2: String(PAD.top + plotH),
        class: 'chart__stage-line',
      }),
    );
  }

  // Direct end-labels, but only when the lines have actually separated.
  const last = rows[rows.length - 1];
  if (last) {
    const nominalY = y(last.balanceNominal);
    const realY = y(last.balanceReal);
    if (Math.abs(nominalY - realY) >= LABEL_GAP) {
      for (const series of SERIES) {
        const value = series.value(last);
        svg.append(
          el('circle', {
            cx: String(x(last.age)),
            cy: String(y(value)),
            r: '4',
            class: `chart__end-dot chart__end-dot--${series.slot}`,
          }),
          label(x(last.age) - 8, y(value) - 10, `${series.label}: ${formatMoney(value, currency)}`, series.slot),
        );
      }
    }
  }

  // Crosshair + hover markers, hidden until the pointer arrives.
  const crosshair = el('line', { class: 'chart__crosshair', y1: String(PAD.top), y2: String(PAD.top + plotH), x1: '0', x2: '0' });
  crosshair.setAttribute('visibility', 'hidden');
  const markers = SERIES.map((series) => {
    const dot = el('circle', { r: '5', class: `chart__marker chart__marker--${series.slot}` });
    dot.setAttribute('visibility', 'hidden');
    return dot;
  });
  svg.append(crosshair, ...markers);

  const tooltip = document.createElement('div');
  tooltip.className = 'chart__tooltip';
  tooltip.hidden = true;

  const children: Node[] = [svg, tooltip, legend()];
  if (boundaryAges.size > 0) children.push(stageNote());
  host.replaceChildren(...children);

  let activeIndex = -1;
  const show = (index: number) => {
    const row = rows[index];
    if (!row) return;
    activeIndex = index;
    const cx = x(row.age);
    crosshair.setAttribute('x1', String(cx));
    crosshair.setAttribute('x2', String(cx));
    crosshair.setAttribute('visibility', 'visible');

    SERIES.forEach((series, i) => {
      const marker = markers[i];
      if (!marker) return;
      marker.setAttribute('cx', String(cx));
      marker.setAttribute('cy', String(y(series.value(row))));
      marker.setAttribute('visibility', 'visible');
    });

    tooltip.replaceChildren(tooltipContent(row, currency));
    tooltip.hidden = false;
    // Flip the tooltip to the left of the crosshair near the right edge.
    const offset = cx > width * 0.6 ? -tooltip.offsetWidth - 12 : 12;
    tooltip.style.transform = `translate(${Math.round(cx + offset)}px, ${PAD.top}px)`;
  };

  const hide = () => {
    activeIndex = -1;
    crosshair.setAttribute('visibility', 'hidden');
    for (const marker of markers) marker.setAttribute('visibility', 'hidden');
    tooltip.hidden = true;
  };

  const indexAt = (clientX: number): number => {
    const box = svg.getBoundingClientRect();
    const scale = width / (box.width || width);
    const localX = (clientX - box.left) * scale;
    return clamp(Math.round(((localX - PAD.left) / plotW) * ageSpan), 0, rows.length - 1);
  };

  svg.addEventListener('pointermove', (event) => show(indexAt(event.clientX)));
  svg.addEventListener('pointerleave', hide);
  svg.addEventListener('focus', () => show(rows.length - 1));
  svg.addEventListener('blur', hide);
  svg.addEventListener('keydown', (event) => {
    const step = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const from = activeIndex === -1 ? rows.length - 1 : activeIndex;
    show(clamp(from + step, 0, rows.length - 1));
  });
}

function tooltipContent(row: YearRow, currency: string): DocumentFragment {
  const frag = document.createDocumentFragment();
  const age = document.createElement('p');
  age.className = 'chart__tooltip-age';
  age.textContent = `Age ${row.age}`;
  frag.append(age);

  for (const series of SERIES) {
    const line = document.createElement('p');
    line.className = 'chart__tooltip-row';

    const key = document.createElement('span');
    key.className = `chart__tooltip-key chart__tooltip-key--${series.slot}`;

    const value = document.createElement('b');
    value.textContent = formatMoney(series.value(row), currency);

    const label = document.createElement('span');
    label.className = 'chart__tooltip-label';
    // Untrusted-by-habit: always textContent, never innerHTML.
    label.textContent = series.label;

    line.append(key, value, label);
    frag.append(line);
  }
  return frag;
}

function stageNote(): HTMLElement {
  const p = document.createElement('p');
  p.className = 'chart__stage-note';
  p.textContent = 'Dashed lines mark a change in contribution stage.';
  return p;
}

function legend(): HTMLElement {
  const list = document.createElement('ul');
  list.className = 'legend';
  for (const series of SERIES) {
    const item = document.createElement('li');
    const key = document.createElement('span');
    key.className = `legend__key legend__key--${series.slot}`;
    const label = document.createElement('span');
    label.textContent = series.label;
    item.append(key, label);
    list.append(item);
  }
  return list;
}

function summarise(p: Projection, currency: string): string {
  const start = p.rows[0];
  return `Line chart of pension pot value by age. From ${formatMoney(start?.balanceNominal ?? 0, currency)} at age ${start?.age ?? 0} to ${formatMoney(p.potNominal, currency)} at age ${p.rows.at(-1)?.age ?? 0}, worth ${formatMoney(p.potReal, currency)} in today's money. Full figures are in the year-by-year table below.`;
}

function line(points: readonly (readonly [number, number])[]): string {
  return points.map(([px, py], i) => `${i === 0 ? 'M' : 'L'} ${round(px)} ${round(py)}`).join(' ');
}

/** 4–5 round numbers covering the range, so the axis reads 0 / 250K / 500K. */
function niceTicks(max: number): number[] {
  const target = 4;
  const rough = max / target;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= rough) ?? magnitude * 10;
  const ticks: number[] = [];
  for (let t = 0; t <= max + step * 0.001; t += step) ticks.push(Number(t.toFixed(6)));
  if ((ticks[ticks.length - 1] ?? 0) < max) ticks.push((ticks[ticks.length - 1] ?? 0) + step);
  return ticks;
}

function ageTicks(first: number, last: number): number[] {
  const span = last - first;
  if (span <= 0) return [first];
  const step = Math.max(1, Math.ceil(span / 6));
  const ticks: number[] = [];
  for (let age = first; age < last; age += step) ticks.push(age);
  // The final age always gets a label, so drop the one before it if they'd collide.
  if (last - (ticks[ticks.length - 1] ?? first) < step * 0.6) ticks.pop();
  ticks.push(last);
  return ticks;
}

function el<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string>,
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(NS, tag);
  for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, value);
  return node;
}

function label(x: number, y: number, content: string, slot: 1 | 2): SVGTextElement {
  const node = text(String(round(x)), String(round(Math.max(PAD.top + 10, y))), content, `chart__end-label chart__end-label--${slot}`);
  node.setAttribute('text-anchor', 'end');
  return node;
}

function text(x: string, y: string, content: string, className: string): SVGTextElement {
  const node = el('text', { x, y, class: className });
  node.textContent = content;
  return node;
}

const round = (n: number) => Math.round(n * 100) / 100;
const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));
